import {Action, NgxsOnInit, Selector, State, StateContext} from '@ngxs/store';
import {
  LoginByToken,
  Logout,
  ToggleSidePanel,
  Search,
  SetFilters,
  SetSelectedEvent,
  IsDashboard
} from './user.actions';
import {Injectable} from '@angular/core';
import {map, tap} from "rxjs/operators";
import {Observable} from "rxjs";
import {AuthContract} from "../../shared/contracts/auth.contract";
import {AuthToken} from "../../shared/models/auth-token.model";
import {StudioUserRoles, User} from "../../shared/models/user.model";
import {FilterOptions} from "../../shared/models/filter-options.model";

export interface UserStore {
  toggleSidePanel: boolean;
  search: string;
  authUser: /** using three flags  */
  /**  auth user */
    User
    /** no auth user */
    | null
    /** request User in progress, its the mark for guards  */
    | undefined;
  authToken: AuthToken | undefined;
  filters?: FilterOptions;
  selectedEvent?: any;
  isDashboard?: boolean;
}

const getAuthUserFromStorage = (): AuthToken => {
  const authTokenString = localStorage.getItem('authToken');
  const authToken = JSON.parse(authTokenString || 'null');

  return authToken;
};

const getNoAuthSate = (): UserStore => ({
  toggleSidePanel: false,
  search: '',
  authUser: null,
  authToken: getAuthUserFromStorage(),
});

@State<UserStore>({
  name: 'user',
  defaults: { ...getNoAuthSate(), authUser: undefined }, // authUser: undefined by default for guards to determine if we already determined the actual auth state
})
@Injectable()
export class UserState implements NgxsOnInit {
  constructor(
    private authContract: AuthContract
  ) {}

  @Selector()
  public static toggleSidePanel(state: UserStore): boolean {
    return state.toggleSidePanel;
  }

  @Selector()
  public static search(state: UserStore): string {
    return state.search;
  }

  @Selector()
  public static authUser(state: UserStore) {
    return state.authUser;
  }

  @Selector()
  public static authToken(state: UserStore): AuthToken | undefined {
    return state.authToken;
  }

  @Selector()
  public static isLoginAs(state: UserStore): boolean {
    return Boolean(state && state.authToken && state.authToken.loginAs);
  }

  @Selector()
  public static filters(state: UserStore): FilterOptions | undefined {
    return state.filters;
  }

  @Selector()
  public static selectedEvent(state: UserStore): any | undefined {
    return state.selectedEvent;
  }

  @Selector()
  public static selectedEventIdFromStorage(): number {
    const selectedEventId = localStorage.getItem('selectedEventId');
    return (selectedEventId) ? Number.parseInt(<string>selectedEventId, 10) : 0;
  };

  @Selector()
  public static isDashboard(state: UserStore): boolean | undefined {
    return state.isDashboard;
  }

  @Selector()
  public static isAdmin(state: UserStore): boolean | null {
    if (state.authUser) {
      const authUser = state.authUser;

      let isStudioAdmin = false;
      if (authUser.studioAccount) {
        const roles = authUser.studioAccount.roles;
        if (roles) {
          const length = roles.length;
          for (let i = 0; i < length; i++) {
            const role = roles[i];
            if ('ROLE_ADMIN' === role.name) {
              isStudioAdmin = true;
              break;
            }
          }
        }
      }

      let isEventAdmin = false;
      if (authUser.eventAccount) {
        isEventAdmin = (authUser.eventAccount.accessLevel === 'ADMIN');
      }

      return (isStudioAdmin || isEventAdmin);
    }

    return null;
  }

  @Selector()
  public static isLeagueAdmin(state: UserStore): boolean | null {
    if (state.authUser) {
      const authUser = state.authUser;

      let isAAULeagueAdmin = false;
      if (authUser.eventAccount && authUser.eventAccount.roleList) {
        const foundLeagueAdmin = authUser.eventAccount.roleList.find((x: any) => x.name === "League Admin");
        if (foundLeagueAdmin) {
          isAAULeagueAdmin = true;
        }
      }

      return isAAULeagueAdmin;
    }

    return null;
  }

  @Selector()
  public static getPrimaryRoleName(state: UserStore) {
    return 'NONE';
  }

  @Action(ToggleSidePanel)
  private toggleSidePanel({ patchState }: StateContext<UserStore>, { toggleSidePanel }: ToggleSidePanel) {
    patchState({ toggleSidePanel });
  }

  @Action(Search)
  private search({ patchState }: StateContext<UserStore>, { search }: Search) {
    patchState({ search });
  }

  @Action(SetFilters)
  private filters({ patchState }: StateContext<UserStore>,  { filters } : SetFilters) {
    patchState({ filters });
  }

  @Action(SetSelectedEvent)
  private setSelectedEvent({ patchState }: StateContext<UserStore>,  { selectedEvent } : SetSelectedEvent) {
    localStorage.setItem('selectedEventId', selectedEvent && String(selectedEvent.id));
    patchState({ selectedEvent });
  }

  @Action(IsDashboard)
  private isDashboard({ patchState }: StateContext<UserStore>, { isDashboard }: IsDashboard) {
    patchState({ isDashboard });
  }

  @Action(LoginByToken)
  private loginByToken({ setState, getState }: StateContext<UserStore>, { token }: LoginByToken) {
    return this.loginByAuthToken(token).pipe(tap((state) => setState(state)));
  }

  @Action(Logout)
  private logout({ setState }: StateContext<UserStore>) {
    localStorage.removeItem('selectedEventId');
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    localStorage.removeItem('selfPersonId');
    setState(getNoAuthSate());
  }

  @Selector()
  public static isEventAdmin(state: UserStore): boolean {
    return false;
  }

  @Selector()
  public static isStudioAdmin(state: UserStore): boolean {
    return this.hasStudioRole(StudioUserRoles.ADMIN, state);
  }

  @Selector()
  public static isStudioOwner(state: UserStore): boolean {
    return this.hasStudioRole(StudioUserRoles.STUDIO_OWNER, state);
  }

  @Selector()
  public static isStudioStaff(state: UserStore): boolean {
    return this.hasStudioRole(StudioUserRoles.STAFF, state);
  }

  @Selector([UserState.isStudioAdmin, UserState.isStudioOwner, UserState.isStudioStaff, UserState.isEventAdmin])
  public static landingUrl(state: UserStore, isAdmin: boolean, isStudioOwner: boolean, isStaff: boolean, isEventAdmin: boolean): string {
    if (!state.authUser) {
      return '/';
    }

    return '/';
  }

  private static hasStudioRole(role: StudioUserRoles, state: UserStore): boolean {
    if (state.authUser && state.authUser.studioAccount && state.authUser.studioAccount.roles) {
      for (let i = 0; i < state.authUser.studioAccount.roles.length; i++) {
        if (role == state.authUser.studioAccount.roles[i].name) {
          return true;
        }
      }
    }
    return false;
  }

  private loginByAuthToken(token: string): Observable<UserStore> {
    // Immediately claim this token in storage to prevent cross-tab race conditions
    // where an older login's slow network request overwrites a newer token.
    const existingAuthTokenStr = localStorage.getItem('authToken');
    let loginAs = false;
    if (existingAuthTokenStr) {
       try {
         const existingAuthToken = JSON.parse(existingAuthTokenStr);
         if (existingAuthToken && existingAuthToken.token === token) {
             loginAs = existingAuthToken.loginAs;
         }
       } catch(e) {}
    }
    const tempAuthToken: AuthToken = { token, loginAs, userId: 0 };
    localStorage.setItem('authToken', JSON.stringify(tempAuthToken));
    localStorage.setItem('token', token);

    return this.authContract.getAuthByToken(token).pipe(map(authUser => {
      const currentTokenInStorage = localStorage.getItem('token');
      const isStillActiveToken = currentTokenInStorage === token;

      if (!authUser) {
        if (isStillActiveToken) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('selfPersonId');
          localStorage.removeItem('token');
        }
        return getNoAuthSate();
      }

      const toggleSidePanel = false;

      const authToken: AuthToken = {token, loginAs: authUser.loginAs, userId: 0};
      
      if (isStillActiveToken) {
        localStorage.setItem('authToken', JSON.stringify(authToken));
        localStorage.setItem('selfPersonId', (authUser?.eventAccount?.selfPersonId ?? 0).toString());
      }

      const search = '';

      return {authUser, authToken, toggleSidePanel, search};
    }));
  }

  public async ngxsOnInit({ getState, dispatch, setState }: StateContext<UserStore>): Promise<void> {
    const authToken = getState().authToken;
    if (authToken) {
      dispatch(new LoginByToken(authToken.token));
    }
  }

}
